/**
 * Root application component and routing setup.
 */
import { useEffect, useState } from "react";
import {
  BroadcastBanner,
  type BroadcastMessage,
} from "./components/BroadcastBanner";
import type { WebsiteSuggestion } from "./components/website-suggest/types";
import type { Website } from "./components/WebsiteManagement";
import type { User as AppUser } from "./app/types";
import { useAuth } from "./contexts/AuthContext";
import { useApp } from "./contexts/AppContext";
import { setTranslationResolver } from "@/utils/translationResolver";
import { getMockWebsites } from "@/services/mockData";
import { broadcastService, toBroadcastMessage } from "@/services/broadcast.service";
import { useAppRouting } from "./app/hooks/useAppRouting";
import { buildSearchQueryString } from "./app/searchNavigation";
import { useBroadcasts } from "./app/hooks/useBroadcasts";
import { AppRoutes } from "./app/AppRoutes";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { ExternalLinkWarningDialog } from "@/components/ExternalLinkWarningDialog";
import { API_URL } from "@/utils/axios";
import { getMaintenanceModeEnabled } from "@/services/publicSystemSettings.service";
import {
  getManagedWebsites,
  getSuggestedWebsites,
  createWebsite,
  updateWebsite as apiUpdateWebsite,
  updateSuggestion as apiUpdateSuggestion,
  updateWebsiteStatus as apiUpdateWebsiteStatus,
  deleteWebsite as apiDeleteWebsite,
  invalidateManagedWebsitesPagedCache,
  type WebsiteStatus,
  type ManagedWebsite,
  type SuggestedWebsiteResponse,
} from "@/services/website.service";
import {
  invalidateSearchSidePanelCountsCache,
  invalidateSearchWebsitesPagedCache,
} from "@/services/searchPage.service";

export type { SearchFocusTarget, User, UserRole } from "./app/types";

type LoginLockInfo = {
  email: string;
  lockedUntil?: string | null;
  messageKey: string;
};

// TODO likely unneccessary, remove later
const initialWebsites: Website[] = getMockWebsites().map((entry) => ({
  id: entry.id,
  title: entry.title,
  url: entry.url,
  description: entry.description,
  addedBy: entry.addedBy,
  addedAt: entry.addedAt,
  region: entry.region,
  zipCode: entry.zipCode,
}));

export default function App() {
  const {
    currentPage,
    searchQuery,
    searchFocusTarget,
    navigate,
    handleNavigateToSearchPage,
    clearSearchFocusTarget,
  } = useAppRouting();
  const { reloadTranslations, language, t } = useApp();
  const [initialBroadcasts, setInitialBroadcasts] = useState<BroadcastMessage[]>([]);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const { activeBroadcast, scheduleBroadcast, dismissBroadcast } =
    useBroadcasts(initialBroadcasts);
  const { user, login, logout } = useAuth();
  const [suggestions, setSuggestions] =
    useState<WebsiteSuggestion[]>([]);
  const [loginLockInfo, setLoginLockInfo] = useState<LoginLockInfo | null>(null);
  const [loginErrorKey, setLoginErrorKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadBroadcasts = async () => {
      try {
        const data = await broadcastService.getVisibleBroadcasts();
        if (!cancelled) {
          setInitialBroadcasts(data.map(toBroadcastMessage));
        }
      } catch (e) {
        console.error("Failed to load broadcasts", e);
      }
    };

    void loadBroadcasts();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    void reloadTranslations();
  }, [reloadTranslations]);

  useEffect(() => {
    setTranslationResolver(t);
    return () => setTranslationResolver(null);
  }, [t]);

  /**
   * maintenance mode
   */
  useEffect(() => {
    let cancelled = false;
    const loadMaintenance = async () => {
      const enabled = await getMaintenanceModeEnabled();
      if (cancelled) return;
      setMaintenanceEnabled(enabled);
      const isAdmin = user?.role === "ADMIN";
      if (enabled && !isAdmin && currentPage !== "login") {
        navigate("login", { replace: true });
      }
    };
    loadMaintenance();
    return () => {
      cancelled = true;
    };
  }, [currentPage, navigate, user]);

  const [websites, setWebsites] = useState<Website[]>([]);
  const invalidatePagedWebsiteCaches = () => {
    invalidateManagedWebsitesPagedCache();
    invalidateSearchWebsitesPagedCache();
    invalidateSearchSidePanelCountsCache();
  };

  const mapManagedWebsite = (website: ManagedWebsite): Website => ({
    id: website.id,
    title: website.title,
    url: website.url,
    description: website.description,
    category1Id: website.category1Id ?? undefined,
    categoryPathIds: website.categoryPathIds ?? undefined,
    categoryPathNames: website.categoryPathNames ?? undefined,
    category1Name: website.category1Name ?? undefined,
    addedBy: website.addedBy ?? undefined,
    addedAt: website.addedAt ?? new Date().toISOString(),
    region: website.region ?? undefined,
    regionId: website.regionId ?? undefined,
    regionPathIds: website.regionPathIds ?? undefined,
    regionPathNames: website.regionPathNames ?? undefined,
    zipCode: website.zipCode ?? undefined,
    address: website.address ?? undefined,
    status: website.status,
  });

  const mapSuggestionStatus = (
    status?: WebsiteStatus | null
  ): WebsiteSuggestion["status"] => {
    switch (status) {
      case "ACTIVE":
        return "approved";
      case "REJECTED":
        return "rejected";
      case "SUGGESTED":
      default:
        return "pending";
    }
  };

  const mapSuggestedWebsite = (
    suggestion: SuggestedWebsiteResponse
  ): WebsiteSuggestion => ({
    id: suggestion.id,
    title: suggestion.title ?? "",
    url: suggestion.urlLandingPage ?? "",
    description: suggestion.description ?? "",
    address: suggestion.address ?? undefined,
    region: suggestion.region ?? "",
    zipCode: suggestion.zipCode ?? undefined,
    category1Id: suggestion.category1Id ?? undefined,
    regionId: suggestion.regionId ?? undefined,
    categoryPathIds: suggestion.categoryPathIds ?? undefined,
    categoryPathNames: suggestion.categoryPathNames ?? undefined,
    regionPathIds: suggestion.regionPathIds ?? undefined,
    regionPathNames: suggestion.regionPathNames ?? undefined,
    category1: suggestion.category1Name ?? "",
    suggestedBy: suggestion.suggestedBy ?? "anonymous",
    suggestedAt: suggestion.addedAt ?? new Date().toISOString(),
    status: mapSuggestionStatus(suggestion.status ?? undefined),
  });

  const refreshSuggestions = async () => {
    try {
      const data = await getSuggestedWebsites(100, language);
      setSuggestions(data.map(mapSuggestedWebsite));
    } catch (e) {
      console.error("Failed to load suggestions", e);
    }
  };

  useEffect(() => {
    if (!user) {
      setSuggestions([]);
      return;
    }
    void refreshSuggestions();
  }, [user, language]);

  useEffect(() => {
    if (!user) {
      setWebsites([]);
      return;
    }
    void refreshWebsites();
  }, [user, language]);

  const refreshWebsites = async () => {
    try {
      const data = await getManagedWebsites(100, language);
      setWebsites(data.map(mapManagedWebsite));
    } catch (e) {
      console.error("Failed to load websites", e);
    }
  };

  const handleEditWebsite = async (id: string, updates: Partial<Website>) => {
    const current = websites.find((w) => w.id === id);
    try {
      if (updates.status) {
        await apiUpdateWebsiteStatus(id, updates.status as WebsiteStatus);
        setWebsites((prev) =>
          prev.map((w) => (w.id === id ? { ...w, status: updates.status } : w))
        );
        invalidatePagedWebsiteCaches();
        return;
      }

      const payload = {
        title: updates.title ?? current?.title ?? "",
        description: updates.description ?? current?.description ?? "",
        url: updates.url ?? current?.url ?? "",
        address: updates.address ?? current?.address ?? null,
        zipCode: updates.zipCode ?? current?.zipCode ?? null,
        category1Id: updates.category1Id ?? current?.category1Id ?? null,
        categoryPathIds: updates.categoryPathIds ?? current?.categoryPathIds ?? null,
        regionId: updates.regionId ?? current?.regionId ?? null,
        regionPathIds: updates.regionPathIds ?? current?.regionPathIds ?? null,
      };

      const updated = await apiUpdateWebsite(id, payload);
      setWebsites((prev) =>
        prev.map((w) => (w.id === id ? mapManagedWebsite(updated) : w))
      );
      invalidatePagedWebsiteCaches();
    } catch (e) {
      console.error("Failed to update website", e);
      throw e;
    }
  };

  const handleDeleteWebsite = async (id: string) => {
    try {
      await apiDeleteWebsite(id);
      setWebsites((prev) => prev.filter((w) => w.id !== id));
      invalidatePagedWebsiteCaches();
    } catch (e) {
      console.error("Failed to delete website", e);
    }
  };

  const resolveWelcomeBackName = (email: string) => {
    try {
      const rawUser = localStorage.getItem("user");
      if (rawUser) {
        const parsed = JSON.parse(rawUser) as
          | { username?: string | null; email?: string | null }
          | null;
        const username = parsed?.username?.trim();
        if (username) {
          return username;
        }
        const userEmail = parsed?.email?.trim();
        if (userEmail) {
          const localPart = userEmail.split("@")[0]?.trim();
          if (localPart) {
            return localPart;
          }
          return userEmail;
        }
      }
    } catch {
      // Ignore malformed local storage user entries.
    }

    const normalized = email.trim();
    const fallbackLocalPart = normalized.split("@")[0]?.trim();
    return fallbackLocalPart || normalized || "User";
  };
  const handleLoginAdapter = async (email: string, pass: string) => {
    try {
      await login({ email, password: pass });
      setLoginLockInfo(null);
      setLoginErrorKey(null);
      const welcomeName = resolveWelcomeBackName(email);
      toast.success(
        t("auth.welcomeBack").replace(/{{\s*name\s*}}/g, welcomeName)
      );
      navigate("home");
    } catch (e: any) {
      setLoginLockInfo(null);
      if (e.response?.status === 403) {
        setLoginErrorKey(null);
        navigate("verify-account", {
          state: { usr: { email: email } },
        });
        return;
      }
      if (e.response?.status === 423) {
        const headers = e.response?.headers ?? {};
        const retryAfterHeader = headers["retry-after"];
        const lockedUntilHeader = headers["x-login-locked-until"];
        const retryAfterSeconds = retryAfterHeader
          ? Number(retryAfterHeader)
          : Number.NaN;
        const lockedUntil =
          lockedUntilHeader ??
          (Number.isFinite(retryAfterSeconds)
            ? new Date(Date.now() + retryAfterSeconds * 1000).toISOString()
            : null);
        const messageKey = e.response?.data?.message ?? "auth.accountLocked";
        setLoginLockInfo({
          email,
          lockedUntil,
          messageKey,
        });
        setLoginErrorKey(null);
        return;
      }
      const errorKey =
        e.response?.status === 401
          ? e.response?.data?.message ?? "auth.invalidCredentials"
          : e.response?.data?.message ?? "common.unexpectedError";
      setLoginErrorKey(errorKey);
    }
  };

  const handleLogoutAdapter = () => {
    logout();
    navigate("home");
  };

  const handleSearch = (query: string) => {
    const search = buildSearchQueryString({ query });
    navigate("search", {
      search,
      state: { searchQuery: query, focusTarget: "search" },
    });
  };

  const handleSuggestWebsite = (suggestion: WebsiteSuggestion) => {
    setSuggestions((prev) => [...prev, suggestion]);
  };

  const handleApproveSuggestion = async (
    id: string,
    modified?: Partial<WebsiteSuggestion>
  ) => {
    const suggestion = suggestions.find((s) => s.id === id);
    if (!suggestion) return;

    const finalSuggestion = modified
      ? { ...suggestion, ...modified }
      : suggestion;

    try {
      if (modified) {
        await apiUpdateWebsite(id, {
          title: finalSuggestion.title,
          description: finalSuggestion.description,
          url: finalSuggestion.url,
          address: finalSuggestion.address ?? null,
          zipCode: finalSuggestion.zipCode ?? null,
          category1Id: finalSuggestion.category1Id ?? null,
          categoryPathIds: finalSuggestion.categoryPathIds ?? null,
          regionId: finalSuggestion.regionId ?? null,
          regionPathIds: finalSuggestion.regionPathIds ?? null,
        });
        setSuggestions((prev) => prev.filter((s) => s.id !== id));
        invalidatePagedWebsiteCaches();
        await refreshWebsites();
        return;
      }
      await apiUpdateWebsiteStatus(id, "ACTIVE");
      setSuggestions((prev) => prev.filter((s) => s.id !== id));
      invalidatePagedWebsiteCaches();
      await refreshWebsites();
    } catch (e) {
      console.error("Failed to approve suggestion", e);
    }
  };

  const handleRejectSuggestion = async (id: string) => {
    try {
      await apiUpdateWebsiteStatus(id, "REJECTED");
      setSuggestions((prev) => prev.filter((s) => s.id !== id));
      invalidatePagedWebsiteCaches();
    } catch (e) {
      console.error("Failed to reject suggestion", e);
    }
  };

  const handleSaveSuggestion = async (
    id: string,
    updates: Partial<WebsiteSuggestion>
  ) => {
    const suggestion = suggestions.find((s) => s.id === id);
    if (!suggestion) return;

    const finalSuggestion = { ...suggestion, ...updates };

    try {
      await apiUpdateSuggestion(id, {
        title: finalSuggestion.title,
        description: finalSuggestion.description,
        url: finalSuggestion.url,
        address: finalSuggestion.address ?? null,
        zipCode: finalSuggestion.zipCode ?? null,
        category1Id: finalSuggestion.category1Id ?? null,
        categoryPathIds: finalSuggestion.categoryPathIds ?? null,
        regionId: finalSuggestion.regionId ?? null,
        regionPathIds: finalSuggestion.regionPathIds ?? null,
      });
      await refreshSuggestions();
    } catch (e) {
      console.error("Failed to save suggestion edits", e);
      throw e;
    }
  };

  const handleAddWebsite = async (
    websiteData: Omit<Website, "id" | "addedAt" | "addedBy">
  ): Promise<void> => {
    try {
      const created = await createWebsite({
        title: websiteData.title,
        description: websiteData.description,
        url: websiteData.url,
        address: websiteData.address ?? null,
        zipCode: websiteData.zipCode ?? null,
        category1Id: websiteData.category1Id ?? null,
        categoryPathIds: websiteData.categoryPathIds ?? null,
        regionId: websiteData.regionId ?? null,
        regionPathIds: websiteData.regionPathIds ?? null,
      });
      setWebsites((prev) => [...prev, mapManagedWebsite(created)]);
      invalidatePagedWebsiteCaches();
    } catch (e) {
      console.error("Failed to create website", e);
      throw e;
    }
  };

  const handleScheduleBroadcast = (broadcast: BroadcastMessage) => {
    scheduleBroadcast(broadcast);
  };

  const handleDismissBroadcast = (id: string) => {
    dismissBroadcast(id);
  };

  const normalizeProfilePictureUrl = (url?: string | null) => {
    if (url == null) return null;
    if (!url.trim()) return null;
    if (url.startsWith("http")) return url;
    try {
      return new URL(url, API_URL).toString();
    } catch {
      return null;
    }
  };

  const appUser: AppUser | null = user
    ? {
        username: user.username,
        email: user.email,
        role: user.role === "ADMIN" ? "admin" : "user",
        // Expose profile picture URL globally; when absent keep it null so no broken fetch is triggered.
        profilePicture: normalizeProfilePictureUrl(user.profilePicture),
        isVerified: user.isVerified,
      }
    : null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        {activeBroadcast && (
          <BroadcastBanner
            message={activeBroadcast}
            onDismiss={() => handleDismissBroadcast(activeBroadcast.id)}
          />
        )}
        <AppRoutes
          currentPage={currentPage}
          navigate={navigate}
          currentUser={appUser}
          maintenanceEnabled={maintenanceEnabled}
          onLogout={handleLogoutAdapter}
          onLogin={handleLoginAdapter}
          loginLockInfo={loginLockInfo}
          onClearLoginLock={() => setLoginLockInfo(null)}
          loginErrorKey={loginErrorKey}
          onClearLoginError={() => setLoginErrorKey(null)}
          onSearch={handleSearch}
          searchQuery={searchQuery}
          focusTarget={searchFocusTarget}
          onFocusHandled={clearSearchFocusTarget}
          onNavigateToSearchPage={handleNavigateToSearchPage}
          suggestions={suggestions}
          onSuggestWebsite={handleSuggestWebsite}
          onApproveSuggestion={handleApproveSuggestion}
          onRejectSuggestion={handleRejectSuggestion}
          onSaveSuggestion={handleSaveSuggestion}
          onAddWebsite={handleAddWebsite}
          onEditWebsite={handleEditWebsite}
          onDeleteWebsite={handleDeleteWebsite}
          onScheduleBroadcast={handleScheduleBroadcast}
        />
        <ExternalLinkWarningDialog />
        <Toaster position="top-right" style={{ top: "5rem" }} closeButton />
    </div>
  );
}
